import { FeatureFlags } from '../generated/feature-flags';
import { useFeatureFlags } from './feature-flags';
import { useAccountData } from './hooks/useAccountData';

/**
 * Optional override for the product-configurator base URL.
 *
 * When SET (e.g. http://localhost:5173/index.html to test a local zakeke-configurator-ui build),
 * every "view configurator" link and preview iframe rewrites the backend-provided URL to this
 * base, keeping the same query params. When UNSET (the deployed builds), the backend-provided
 * URL is used AS-IS, so every surface — shopping preview, UI-settings live preview, order
 * pages — points at the environment-correct configurator host; only query params are adjusted.
 */
export const NEW_CONFIGURATOR_BASE_URL = process.env.NEW_CONFIGURATOR_BASE_URL || '';

/**
 * Name of the Azure App Configuration feature flag that reveals the new/old configurator
 * URL switch. When the flag is OFF (the default for everyone) only the new URL is used and
 * no switch / "(old)" entry is shown.
 *
 * NOTE: this flag must also exist in Azure App Configuration to be remotely toggleable;
 * until then it defaults to OFF (see {@link useConfiguratorUrlSwitchEnabled}).
 */
export const CONFIGURATOR_URL_SWITCH_FLAG: string = 'configuratorUrlSwitch';

/**
 * Rewrites a backend-provided configurator URL so it targets the {@link NEW_CONFIGURATOR_BASE_URL}
 * override, preserving the same query string and hash (i.e. the same params). When no override is
 * configured, the backend URL is returned unchanged — the base stays the same one the shopping
 * preview uses for the current environment.
 *
 * Falls back gracefully to the override base URL if the input cannot be parsed.
 */
export const toNewConfiguratorUrl = (oldUrl: string | null | undefined): string => {
    if (!oldUrl) {
        return oldUrl ?? '';
    }

    if (!NEW_CONFIGURATOR_BASE_URL) {
        return oldUrl;
    }

    try {
        const parsed = new URL(oldUrl);
        return `${NEW_CONFIGURATOR_BASE_URL}${parsed.search}${parsed.hash}`;
    } catch {
        // Relative / unparseable URL: keep everything from the first '?' or '#' (query + hash params).
        const markerIndices = [oldUrl.indexOf('?'), oldUrl.indexOf('#')].filter((index) => index >= 0);
        const startIndex = markerIndices.length > 0 ? Math.min(...markerIndices) : -1;
        const params = startIndex >= 0 ? oldUrl.slice(startIndex) : '';
        return `${NEW_CONFIGURATOR_BASE_URL}${params}`;
    }
};

/** Query parameter the new configurator reads to render the old interface instead of the new one. */
export const FORCE_OLD_INTERFACE_PARAM = 'forceInterface';
export const FORCE_OLD_INTERFACE_VALUE = 'old';
/** Query value that forces the new configurator interface (mirrors the composer admin "Force new UI" option, i.e. `forceInterface=new`). */
export const FORCE_NEW_INTERFACE_VALUE = 'new';

/**
 * Builds the configurator URL for an iframe/link: always the new configurator (keeping the same params),
 * with `forceInterface=old` appended when the user has toggled to the old interface. The param is inserted
 * into the query string (before any hash) without re-encoding the existing params.
 */
export const toConfiguratorUrl = (backendUrl: string | null | undefined, useOldInterface: boolean): string => {
    const url = toNewConfiguratorUrl(backendUrl);
    if (!useOldInterface || !url) {
        return url;
    }

    const hashIndex = url.indexOf('#');
    const beforeHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
    const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
    const separator = beforeHash.includes('?') ? '&' : '?';
    return `${beforeHash}${separator}${FORCE_OLD_INTERFACE_PARAM}=${FORCE_OLD_INTERFACE_VALUE}${hash}`;
};

/**
 * Forces the new configurator interface by appending `forceInterface=new` (the precise "Force new UI"
 * mechanism the configurator reads). Any existing `forceInterface` param is removed first so the value
 * is unambiguous. Returns the input unchanged when it is empty.
 */
export const withForcedNewInterface = (url: string | null | undefined): string => {
    if (!url) {
        return url ?? '';
    }

    const cleaned = removeQueryParam(url, FORCE_OLD_INTERFACE_PARAM);
    const hashIndex = cleaned.indexOf('#');
    const beforeHash = hashIndex >= 0 ? cleaned.slice(0, hashIndex) : cleaned;
    const hash = hashIndex >= 0 ? cleaned.slice(hashIndex) : '';
    const separator = beforeHash.includes('?') ? '&' : '?';
    return `${beforeHash}${separator}${FORCE_OLD_INTERFACE_PARAM}=${FORCE_NEW_INTERFACE_VALUE}${hash}`;
};

/**
 * Adds (or replaces) a single query parameter on a URL, preserving the order and encoding of the
 * other params and any hash. Any existing occurrence of the key (case-insensitive) is removed first
 * so the value is unambiguous. Returns the input unchanged when it is empty.
 */
export const setQueryParam = (url: string | null | undefined, param: string, value: string): string => {
    if (!url) {
        return url ?? '';
    }

    const cleaned = removeQueryParam(url, param);
    const hashIndex = cleaned.indexOf('#');
    const beforeHash = hashIndex >= 0 ? cleaned.slice(0, hashIndex) : cleaned;
    const hash = hashIndex >= 0 ? cleaned.slice(hashIndex) : '';
    const separator = beforeHash.includes('?') ? '&' : '?';
    return `${beforeHash}${separator}${param}=${value}${hash}`;
};

/**
 * Removes a single query parameter from a URL (case-insensitive on the key), preserving the order and
 * encoding of the remaining params and any hash.
 */
export const removeQueryParam = (url: string | null | undefined, param: string): string => {
    if (!url) {
        return url ?? '';
    }

    const hashIndex = url.indexOf('#');
    const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
    const beforeHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
    const queryIndex = beforeHash.indexOf('?');
    if (queryIndex < 0) {
        return url;
    }

    const base = beforeHash.slice(0, queryIndex);
    const kept = beforeHash
        .slice(queryIndex + 1)
        .split('&')
        .filter((pair) => pair.length > 0 && pair.split('=')[0].toLowerCase() !== param.toLowerCase());
    return `${base}${kept.length > 0 ? `?${kept.join('&')}` : ''}${hash}`;
};

/**
 * Whether the new/old configurator URL switch should be shown for the current user.
 *
 * Gated by the {@link CONFIGURATOR_URL_SWITCH_FLAG} feature flag. Defensive against the flag
 * being absent (e.g. before it has been created in Azure App Configuration, or after the
 * generated flags file is regenerated without it): in that case it returns false, so only
 * the new configurator URL is used.
 */
export const useConfiguratorUrlSwitchEnabled = (): boolean => {
    const { userInfo } = useAccountData();
    const { featureFlags, isEnabledWithConditions } = useFeatureFlags();

    const flagKey = CONFIGURATOR_URL_SWITCH_FLAG as keyof FeatureFlags;
    if (!featureFlags[flagKey]) {
        return false;
    }

    return isEnabledWithConditions(flagKey, { userId: userInfo?.userID?.toString() });
};
